import { GetOtherProductsQueryVariables } from '@app/data/shop-bff';
import { CarouselMediaCardProps } from 'components/CarouselSection/types';
import { partition } from 'lodash';
import { useCallback, useState } from 'react';
import {
  DefaultInventoryItem,
  ListsButtonInventoryItem,
  ListsInclusions,
  OtherProducts,
  Work,
} from 'types/shop-bff/types';
import { PriceProps } from '../../../../components/Price';
import {
  getCustomPublicShopBffSdk,
  getPreviewUrl,
} from '../../../../utils/common';
import { OTHER_PRODUCTS_ENDPOINT } from '../../../api/shop-bff/route-constants';
import { PreviewStyle } from '../../constants';
import { categoryProductStyleList } from '../ProductStyleSwitcher';

export const mapDefaultInventoryItemToMediaCardProps = (
  work: Work,
  listsInclusions: ListsInclusions | null,
  seoLinkLimit?: number
) => {
  return (inputData, _, index): CarouselMediaCardProps | null => {
    if (!inputData) return null;
    const hiddenLink =
      seoLinkLimit === 0 ||
      (seoLinkLimit !== undefined && seoLinkLimit <= index);

    return {
      productId: inputData?.id,
      link:
        inputData.productPageUrls?.url ||
        inputData.productPageUrls?.fallbackUrl ||
        '',
      imageSrc: getPreviewUrl(inputData, PreviewStyle.ALTERNATE_PRODUCT_CLOSE),
      title: inputData.description,
      workTitle: inputData.description,
      artistName: work.artistUsername,
      matureContent: work.matureContent,
      caption: '',
      loading: false,
      testId: 'aao-carousel-card',
      addToListButtonProps: {
        inventoryItem: inputData as ListsButtonInventoryItem,
        workId: work.id,
        listsInclusions,
      },
      hideLink: hiddenLink,
      stockStatus: inputData.stockStatus || null,
    };
  };
};

export const groupSwitchableAndUnswitchableProducts = (
  productTypeId: string,
  items: DefaultInventoryItem[]
): [
  Array<NonNullable<DefaultInventoryItem>>,
  Array<NonNullable<DefaultInventoryItem>>,
] => {
  const nonNullItems = items.filter(Boolean) as Array<
    NonNullable<DefaultInventoryItem>
  >;

  const switchableCategory = Object.keys(categoryProductStyleList).find(
    (key) => {
      if (categoryProductStyleList[key].includes(productTypeId)) {
        return key;
      }
      return null;
    }
  );

  if (!switchableCategory) return [[], nonNullItems];

  return partition(nonNullItems, (item) =>
    categoryProductStyleList[switchableCategory].includes(item?.productTypeId)
  );
};

export const mapPriceProps = (
  input: DefaultInventoryItem
): PriceProps | null => {
  if (!input?.price) return null;

  const { discount, currency, amount } = input.price;

  return {
    discount,
    currency,
    amount,
  };
};

const otherProductsSdk = getCustomPublicShopBffSdk(OTHER_PRODUCTS_ENDPOINT);

export const useOtherProducts = () => {
  const [{ otherProducts, loading }, setOtherProducts] = useState<{
    otherProducts: OtherProducts | null;
    loading: boolean;
  }>({
    otherProducts: null,
    loading: true,
  });

  const refresh = useCallback((variables: GetOtherProductsQueryVariables) => {
    setOtherProducts({ otherProducts: null, loading: true });
    otherProductsSdk
      .GetOtherProducts(variables)
      .then((res) => {
        setOtherProducts({
          otherProducts: res.shop_otherProducts,
          loading: false,
        });
      })
      .catch(() => {
        // TODO: DEP-3040: Handle errors
        setOtherProducts({
          otherProducts: null,
          loading: false,
        });
      });
  }, []);

  return {
    otherProducts,
    loading,
    refresh,
  };
};
